import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useFetchTeam } from '@/hooks/useFetchTeam';
import { useAuthStore } from '@/stores/AuthStore';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { AppBootstrapSplash } from '@/components/AppBootstrapSplash';
import { setAnalyticsIdentity, trackAnalyticsPageView } from '@/analytics/client';
import { getAnalyticsIdentity } from '@/analytics/identity';
import { getPageTrackingData } from '@/analytics/routeTracking';

const ResolvedTeamRoutePageView = () => {
  const location = useLocation();

  useEffect(() => {
    const { status } = useAuthStore.getState();
    setAnalyticsIdentity(getAnalyticsIdentity(status === 'authenticated'));
    trackAnalyticsPageView(
      location.pathname,
      location.search,
      getPageTrackingData(location.pathname)
    );
  }, [location.key, location.pathname, location.search]);

  return null;
};

export function TeamEntryBootstrapGate() {
  const location = useLocation();
  const sessionState = useAuthStore(s => s.sessionState);
  const userId = useAuthStore(s => s.userId);
  const { id: tournamentCalendarId, hasResolved: hasResolvedTournamentCalendar } =
    useTeamCreateTournamentCalendarId();
  const [hasReleasedBootstrap, setHasReleasedBootstrap] = useState(false);

  const isAuthenticated = sessionState === 'authenticated';
  const isMyTeamRoute =
    location.pathname === '/my-team' || location.pathname.startsWith('/my-team/');
  const isViewingOtherTeam = new URLSearchParams(location.search).has('teamId');

  const existingTeamQuery = useFetchTeam(userId ?? undefined, tournamentCalendarId ?? undefined, {
    enabled: hasResolvedTournamentCalendar && isAuthenticated && !!userId,
  });

  const requiresTeamLookup = isAuthenticated && !!userId;
  const isAuthSettled = sessionState !== 'resolving';
  const isTeamLookupSettled = !requiresTeamLookup || !existingTeamQuery.isPending;

  const shouldHoldBootstrap =
    !hasResolvedTournamentCalendar || !isAuthSettled || !isTeamLookupSettled;

  useEffect(() => {
    if (!hasReleasedBootstrap && !shouldHoldBootstrap) {
      setHasReleasedBootstrap(true);
    }
  }, [hasReleasedBootstrap, shouldHoldBootstrap]);

  const bootstrapDecision = useMemo(() => {
    if (shouldHoldBootstrap && !hasReleasedBootstrap) {
      return 'loading' as const;
    }

    if (isMyTeamRoute) {
      if (sessionState === 'anonymous' || !userId) {
        return 'redirect-root' as const;
      }

      // Allow viewing another manager's team even if the logged-in user has no team
      if (isAuthenticated && !existingTeamQuery.data && !isViewingOtherTeam) {
        return 'redirect-root' as const;
      }
    } else if (isAuthenticated && existingTeamQuery.data) {
      return 'redirect-my-team' as const;
    }

    return 'render' as const;
  }, [
    existingTeamQuery.data,
    hasReleasedBootstrap,
    isAuthenticated,
    isMyTeamRoute,
    isViewingOtherTeam,
    sessionState,
    shouldHoldBootstrap,
    userId,
  ]);

  if (bootstrapDecision === 'loading') {
    return (
      <div data-team-entry-bootstrap="loading">
        <AppBootstrapSplash message="Securing your session and team before the experience loads." />
      </div>
    );
  }

  if (bootstrapDecision === 'redirect-root') {
    return <Navigate to="/" replace />;
  }

  if (bootstrapDecision === 'redirect-my-team') {
    return <Navigate to="/my-team" replace />;
  }

  return (
    <>
      <ResolvedTeamRoutePageView />
      <Outlet />
    </>
  );
}
